import React, { useEffect, useState } from 'react';
import { Award, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import VideoBackgroundHero from './VideoBackgroundHero';

const MASCOT_URL = 'https://customer-assets.emergentagent.com/job_bezalel-ux-update/artifacts/k54w94pu_bezalel.png';
const MASCOT_STYLE = { filter: 'brightness(1.1) drop-shadow(0 0 30px rgba(6, 182, 212, 0.45))' };

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    const el = document.querySelector('#contacto');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToServices = (e) => {
    e.preventDefault();
    const el = document.querySelector('#services');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col overflow-hidden"
    >
      {/* ── Video Background ── */}
      <VideoBackgroundHero />

      {/* ── Glow blobs ── */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-transparent via-cyan-400/10 to-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* ══════════════════════════════════════════
          DESKTOP MASCOT — absolute right column
      ══════════════════════════════════════════ */}
      <div className="hidden md:block absolute right-16 top-1/2 max-[1366px]:top-[55%] -translate-y-1/2 z-20">
        <div className="relative">
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-48 h-4 bg-black/40 rounded-full blur-lg" />
          <div className="absolute -inset-12 bg-gradient-to-br from-cyan-400/15 via-blue-500/10 to-transparent rounded-full blur-[80px] -z-10" />
          <img
            src={MASCOT_URL}
            alt="Bezalel - Tejón Tecnológico"
            className="w-56 lg:w-[480px] xl:w-[546px] h-auto drop-shadow-2xl"
            style={MASCOT_STYLE}
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════════ */}
      <div className={`
        flex-1 flex flex-col justify-start md:justify-center
        container mx-auto px-6 sm:px-8 z-20 relative
        pt-28 md:pt-0
        lg:grid lg:grid-cols-2 lg:items-center lg:gap-8
        transform transition-all duration-1000
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
      `}>

        {/* ── Left / top text block ── */}
        <div className="lg:max-w-2xl">

          {/* Title — bicolor */}
          <h1 className="text-[2.1rem] leading-[1.15] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 md:mb-5">
            <span className="text-white">Innovación Tecnológica </span>
            <span className="text-cyan-400">para tu Éxito Digital</span>
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-7 max-w-md leading-relaxed">
            Infraestructura, Desarrollo y Seguridad potenciados con IA. Todo lo que tu empresa necesita en un solo lugar.
          </p>

          {/* ── CTA Buttons — BOTH on mobile ── */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Primary */}
            <Button
              onClick={scrollToContact}
              size="lg"
              className="
                bg-gradient-to-r from-cyan-500 to-blue-600
                hover:from-cyan-400 hover:to-blue-500
                text-white font-semibold text-base
                px-8 min-h-[52px] rounded-full w-full sm:w-auto
                shadow-lg shadow-cyan-500/30
                transition-all duration-300
              "
            >
              ¡Contáctanos Hoy! →
            </Button>

            {/* Secondary — visible on mobile too */}
            <Button
              onClick={scrollToServices}
              size="lg"
              variant="outline"
              className="
                bg-transparent border-2 border-white/50
                text-white hover:bg-white/10 hover:border-white/80
                font-semibold text-base
                px-8 min-h-[52px] rounded-full w-full sm:w-auto
                transition-all duration-300
              "
            >
              Ver Servicios
            </Button>
          </div>

          {/* ── Desktop Stats (inline) ── */}
          <div className="hidden md:flex mt-10 pt-10 border-t border-white/20 items-center gap-6">
            <div className="text-left">
              <div className="text-4xl font-bold text-cyan-400 mb-0.5">7+</div>
              <div className="text-sm text-white/80">Años de Experiencia</div>
            </div>
            <div className="h-10 w-px bg-white/20" />
            <div className="text-left">
              <div className="text-4xl font-bold text-cyan-400 mb-0.5">50+</div>
              <div className="text-sm text-white/80">Proyectos Entregados</div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          MOBILE MASCOT — between buttons & stats
      ══════════════════════════════════════════ */}
      <div className="md:hidden relative z-20 flex justify-center px-6 mt-4 mb-2">
        <div className="relative">
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-40 h-4 bg-black/30 rounded-full blur-lg" />
          <div className="absolute -inset-8 bg-gradient-to-br from-cyan-400/10 via-blue-500/5 to-transparent rounded-full blur-[50px] -z-10" />
          <img
            src={MASCOT_URL}
            alt="Bezalel - Tejón Tecnológico"
            className="w-72 h-auto drop-shadow-xl"
            style={MASCOT_STYLE}
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════
          MOBILE STATS — glassmorphism cards
      ══════════════════════════════════════════ */}
      <div className="md:hidden z-20 relative px-6 pb-10">
        <div className="grid grid-cols-2 gap-4">

          {/* Card 1 */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 flex flex-col items-center text-center shadow-lg">
            <Award className="w-8 h-8 text-cyan-400 mb-2" />
            <div className="text-3xl font-extrabold text-cyan-400 leading-none">7+</div>
            <div className="text-xs text-white/75 mt-2 leading-snug">Años de<br />Experiencia</div>
          </div>

          {/* Card 2 */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 flex flex-col items-center text-center shadow-lg">
            <Rocket className="w-8 h-8 text-cyan-400 mb-2" />
            <div className="text-3xl font-extrabold text-cyan-400 leading-none">50+</div>
            <div className="text-xs text-white/75 mt-2 leading-snug">Proyectos<br />Entregados</div>
          </div>
        </div>
      </div>

      {/* ── Scroll indicator (desktop only) ── */}
      <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
